import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactGA from 'react-ga4';
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import PortalMenu from "../../components/PortalMenu/PortalMenu";
import CompiledComponents from "../../utils/CompiledComponents";
import Breadcrumb from "../../components/common/Breadcrumb/Breadcrumb";

import { GetElsLookupsState } from "../../redux/reducers/getLookups/getElsLookupsReducer";
import { getElsLookups, getLookups } from "../../redux/actions/lookups/lookups";
import './Portal.css';
import LoadingSpinner from "../../components/common/LoadingSpinner/LoadingSpinner";
import { ReadOnlyProvider } from "../../utils/ReadOnlyContext";
import { ReadOnlyState } from "../../redux/reducers/getReadOnly/readOnlyReducer";
import { SaveDataState } from "../../redux/reducers/getSaveData/saveDataReducer";
import { LoadingState } from "../../redux/reducers/getLoadingReducer/getLoadingReducer";
import { LookupsState } from "../../redux/reducers/getLookups/lookupsReducer";
import resetOnRefresh from "../../redux/reducers/getLoadingReducer/resetOnRefreshReducer";
import { ProfileOverviewState } from "../../redux/reducers/getProfile/getProfileOverviewReducer";
import { setChangesMade } from "../../redux/actions/saveData/saveData";
import MenuItem from "../../types/menu/MenuItem";
import { RESET_GET_PREVIOUS_SNAPSHOT_DATA, RESET_GET_PREVIOUS_SNAPSHOT_DATA_INTERNAL, RESET_LOOKUPS } from "../../redux/actionTypes/actionsTypes";
import { getPreviousSnapshotData, getPreviousSnapshotDataInternal } from "../../redux/actions/snapshot/getFullSnapshotData";
import { selectELSLookups, selectGetLoading, selectGetPreviousSnapshotData, selectGetPreviousSnapshotDataInternal, selectLookups, selectMenu, selectProfileGetOverview, selectReadOnly, selectSaveData } from "../../redux/selectors/exportSelectorPaths";
import { setActiveMenuItem } from "../../redux/actions/menu/menu";
import { initialMenuItem } from "../../redux/reducers/getMenu/getMenuReducer";
import reportMenuDuration from "../../utils/GA4EventHelpers/reportMenuDuration";
import { PreviousSnapshotState } from "../../redux/reducers/getSnapshot/getPreviousSnapshotDataReducer";
import { PreviousSnapshotInternalState } from "../../redux/reducers/getSnapshot/getPreviousSnapshotDataInternalReducer";
import { CampaignType } from "../../types/profile/Campaign";

export const needSelectOnFocus = (_activeMenuItem: MenuItem) => {
  return true
}

function Portal(): JSX.Element {
  const dispatch = useDispatch();
  const menuState = useSelector(selectMenu);
  const readOnlyState: ReadOnlyState = useSelector(selectReadOnly)
  const [DynamicComponent, setDynamicComponent] = useState<JSX.Element>();
  const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
  const lookupState: LookupsState = useSelector(selectLookups);
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
  const saveData: SaveDataState = useSelector(selectSaveData);
  const loadingState: LoadingState = useSelector(selectGetLoading);
  const isInternalUser = sessionStorage.getItem("isInternalUser") ? true : false;
  const campaignId = sessionStorage.getItem("campaignId")
  const propertyCode = sessionStorage.getItem("propertyCode") as string
  const rollNumber = sessionStorage.getItem("rollNumber")
  const campaignYear = sessionStorage.getItem("activeCampaignYear")
  const profileId = sessionStorage.getItem("profileId")
  const previousSnapshotState: PreviousSnapshotState = useSelector(selectGetPreviousSnapshotData);
  const previousSnapshotInternalState: PreviousSnapshotInternalState = useSelector(selectGetPreviousSnapshotDataInternal);
  const portalRef = useRef(null);
  const profileTypeCode = profileOverview.profile.profile_type_code

  useEffect(() => {
    if (campaignId) {
      sessionStorage.removeItem("previousSnapshotData")
      dispatch({ type: RESET_GET_PREVIOUS_SNAPSHOT_DATA })
      Object.keys(sessionStorage)
        .filter(key => key.includes("previousSnapshotDataInternal"))
        .forEach(key => sessionStorage.removeItem(key));
      dispatch({ type: RESET_GET_PREVIOUS_SNAPSHOT_DATA_INTERNAL })
      dispatch(getPreviousSnapshotData(campaignId))
      dispatch(getPreviousSnapshotDataInternal(rollNumber as string, campaignYear as string, profileId as string))
    }
  }, [campaignId, dispatch, rollNumber, campaignYear, profileId])

  useEffect(() => {
    if (!isInternalUser && !previousSnapshotState.loading && previousSnapshotState.status) {
      if (previousSnapshotState.status === 200 && previousSnapshotState.data) {
        if (sessionStorage.getItem("activeCampaignYear") === process.env.REACT_APP_ACTIVE_CAMPAIGN_YEAR) {
          ReactGA.gtag("set", "user_properties", {
            campaign_start_type: previousSnapshotState.found === true ? "import" : "new"
          })
        }
        const previousSnapshot = {
          // "comments": previousSnapshotState.data["comments"],
          "incomeAttribute": previousSnapshotState.data["incomeAttribute"],
          "profileAttributes": previousSnapshotState.data["profileAttributes"],
          "snapshot": previousSnapshotState.data["snapshot"],
          "campaign": previousSnapshotState.data["campaign"]
        }
        const jsonPreviousSnapshot = JSON.stringify(previousSnapshot)
        if (jsonPreviousSnapshot.length > 2) {
          sessionStorage.setItem("previousSnapshotData", jsonPreviousSnapshot)
        }
      }
      dispatch(dispatch({ type: RESET_GET_PREVIOUS_SNAPSHOT_DATA }))
    }
  }, [dispatch, previousSnapshotState, isInternalUser])

  useEffect(() => {
    if (isInternalUser && !previousSnapshotInternalState.loading && previousSnapshotInternalState.status) {
      if (previousSnapshotInternalState.status === 200 && previousSnapshotInternalState.data) {
        if (sessionStorage.getItem("activeCampaignYear") === process.env.REACT_APP_ACTIVE_CAMPAIGN_YEAR) {
          ReactGA.gtag("set", "user_properties", {
            campaign_start_type: previousSnapshotInternalState.found === true ? "import" : "new"
          })
        }
        for (const i in previousSnapshotInternalState.data) {
          const data = previousSnapshotInternalState.data[i] as PreviousSnapshotState["data"]
          if (data) {
            const year = (data["campaign"] as CampaignType)["campaignYear"]
            const previousSnapshot = {
              "incomeAttribute": data["incomeAttribute"],
              "profileAttributes": data["profileAttributes"],
              "snapshot": data["snapshot"],
              "campaign": data["campaign"]
            }
            const jsonPreviousSnapshot = JSON.stringify(previousSnapshot)
            if (jsonPreviousSnapshot.length > 2) {
              sessionStorage.setItem("previousSnapshotDataInternal" + year, jsonPreviousSnapshot)
            }
          }
        }
      }
      dispatch(dispatch({ type: RESET_GET_PREVIOUS_SNAPSHOT_DATA_INTERNAL }))
    }
  }, [previousSnapshotInternalState, dispatch, isInternalUser])

  useEffect(() => {
    if (menuState.activeItem.componentName) {
      const findById = document.getElementById("portal-content");
      if (findById) {
        findById.scrollIntoView()
      }
      setDynamicComponent(CompiledComponents[menuState.activeItem.componentName]);
    }
  }, [menuState.activeItem.componentName])

  useEffect(() => {
    if (!elsLookupsState.loading && !elsLookupsState.success && !["GEV"].includes(profileOverview.profile.profile_type_code)) { // disable ELS calls for GEV portfolio
      dispatch(getElsLookups(propertyCode));
    }
  }, [dispatch, elsLookupsState, profileOverview.profile.profile_type_code, propertyCode]);

  useEffect(() => {
    const profileTypeCode = profileOverview.profile.profile_type_code.toLowerCase()
    const lookupType = profileTypeCode + "-income-attributes";
    if (!lookupState.loading && (lookupState.lookupType !== lookupType || !lookupState.success)
      && (["bip", "glf", "htl", "lle", "ltc", "mtl", "rho"].includes(profileTypeCode))
    ) {
      dispatch({ type: RESET_LOOKUPS });
      dispatch(getLookups(lookupType));
    }
  }, [dispatch, lookupState, profileOverview.profile.profile_type_code])

  useEffect(() => {
    const alertUser = (e: { preventDefault: () => void; returnValue: string; }) => {
      e.preventDefault();
      if (loadingState.loading < 0) {
        dispatch(resetOnRefresh())
        dispatch(setActiveMenuItem(initialMenuItem))
        reportMenuDuration(menuState, "Home");
      }
      e.returnValue = "";
    }
    if (!readOnlyState.isReadOnly) {
      window.addEventListener("beforeunload", alertUser);
    }
    return () => {
      window.removeEventListener("beforeunload", alertUser);
    };
  }, [dispatch, loadingState.loading, readOnlyState.isReadOnly, menuState]);

  const portalOnChange = (_e: React.FormEvent) => {
    dispatch(setChangesMade())
  }

  const portalOnFocus = (e: React.FocusEvent) => {
    // console.log("portal onFocus", e)
    if (needSelectOnFocus(menuState.activeItem)) {
      const target = e.target as Element
      if (target instanceof HTMLInputElement) {
        target.select()
      }
    }
  }

  const handleEnterKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const portalContainer = portalRef.current;
      if (portalContainer) {
        const fields = Array.from((portalContainer as HTMLDivElement).querySelectorAll('input'));
        const focusedIndex = (document.activeElement && document.activeElement.tagName.toLowerCase() === 'input') ? fields.indexOf(document.activeElement as HTMLInputElement) : -1;
        if (focusedIndex > -1 && focusedIndex < fields.length - 1) {
          fields[focusedIndex + 1].focus();
        } else if (focusedIndex < 0 && fields.length > 0) {
          fields[0].focus();
        }
      }
    }
  }

  return (
    <LoadingSpinner loading={saveData.saving || loadingState.loading !== 0}>
      <div className={'portal-root-layout'}>
        <ReadOnlyProvider isInitiallyReadOnly={readOnlyState.isReadOnly}>
          <div className="portal-root-body">

            <div className="portal-sidebar">
              <PortalMenu />
            </div>

            <div className={'portal-root-content'}>
              <div className={'portal-header'}>
                <div className="">
                  <Header showAutoSave={isInternalUser ? false : true}
                    isInternalUser={isInternalUser} />
                </div>
              </div>
              <div className="portal-breadcrumb">
                <Breadcrumb profileTypeCode={profileTypeCode} />
              </div>
              <div className={'portal-content'} id={'portal-content'} ref={portalRef}
                onChange={(e) => portalOnChange(e)}
                onFocus={(e) => portalOnFocus(e)}
                onKeyDown={(e) => handleEnterKey(e)}
              >
                {menuState.activeItem.componentName && DynamicComponent}
              </div>
            </div>

          </div>
          <div className={'portal-root-footer'}>
            <div className={'portal-footer'}>
              <Footer />
            </div>
          </div>
        </ReadOnlyProvider>
      </div>
    </LoadingSpinner>
  );
}

export default Portal;
